import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Heart, DollarSign, Repeat, Gift, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—");

const STATUS_STYLES = {
  paid: "bg-green-100 text-green-800 border-green-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  expired: "bg-slate-100 text-slate-600 border-slate-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

const Donations = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/donations`)
      .then((res) => { setItems(res.data.items || []); setSummary(res.data.summary || {}); })
      .catch(() => toast.error("Failed to load donations"))
      .finally(() => setLoading(false));
  }, []);

  const cards = [
    { label: "Total Raised", value: money(summary.total_raised), sub: `${summary.total_count || 0} donations`, icon: DollarSign, color: "text-green-600", bg: "bg-green-100" },
    { label: "Monthly Recurring", value: money(summary.monthly_mrr), sub: `${summary.monthly_count || 0} monthly donors`, icon: Repeat, color: "text-blue-600", bg: "bg-blue-100" },
    { label: "One-time Gifts", value: money(summary.one_time_total), sub: `${summary.one_time_count || 0} gifts`, icon: Gift, color: "text-amber-600", bg: "bg-amber-100" },
  ];

  return (
    <div className="space-y-6" data-testid="donations-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>Donations</h1>
        <p className="text-slate-500 mt-1">Track gifts from donors and recurring supporters</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {cards.map((c) => (
          <Card key={c.label} className="dashboard-widget stat-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`w-12 h-12 ${c.bg} rounded-xl flex items-center justify-center`}>
                <c.icon className={`w-6 h-6 ${c.color}`} />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900" data-testid={`stat-${c.label.replace(/ /g, '-').toLowerCase()}`}>{c.value}</div>
                <div className="text-sm text-slate-500">{c.label}</div>
                <div className="text-xs text-slate-400">{c.sub}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-600" />Recent Donations</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 py-8 text-center">Loading...</p>
          ) : items.length === 0 ? (
            <div className="py-12 text-center" data-testid="no-donations">
              <Heart className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No donations yet. Share your donate page to start receiving gifts.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Donor</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.session_id} data-testid={`donation-row-${d.session_id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{d.donor_name || "Anonymous"}</div>
                      {d.donor_email && <div className="text-xs text-slate-400">{d.donor_email}</div>}
                    </TableCell>
                    <TableCell className="font-semibold text-slate-900">{money(d.amount)}{d.frequency === "monthly" && <span className="text-xs text-slate-400">/mo</span>}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={d.frequency === "monthly" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                        {d.frequency === "monthly" ? "Monthly" : "One-time"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtDate(d.created_at)}</TableCell>
                    <TableCell><Badge variant="outline" className={STATUS_STYLES[d.payment_status] || STATUS_STYLES.pending}>{d.payment_status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Donations;
